/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import cloneDeep from 'lodash.clonedeep';
import isArray from 'lodash.isarray';
import isNil from 'lodash.isnil';
import isObject from 'lodash.isobject';
import {serializeMessageEndpoint} from '@ion/applications/src/pages/admin/accounts/MessageEndpointUtils';
import {deserialize as deserializeSSO, getDomainValue, serializeSSOApps, SSOFormFields} from '@ion/applications/src/pages/admin/accounts/SSOEditorUtil';
import {Entry} from '@ion/applications/src/pages/admin/accounts/Users';
import {get, getEnvironment} from '@ion/core/src/environment/environment';
import {getLanguageKey} from '@ion/core/src/locale/locale';
import {
  entityId,
  parseExtendedAttributes,
  setRel
} from '@ion/core/src/services/ConnectUtils';
import {getOperatorIdType} from '@ion/core/src/services/GroupProperties';
import {
  Account,
  AccountLink,
  Contact,
  EntityStatus,
  GenericObject,
  Link,
  SSOConfiguration
} from '@ion/core/src/types/entities';
import {EmissionTester} from '@ion/core/src/types/services';
import {
  getDefaultDateFormat
} from '@ion/core/src/utils/DateUtil';
import {
  isObjectEmpty
} from '@ion/core/src/utils/ObjectUtil';
import {
  isEmpty
} from '@ion/core/src/utils/StringUtil';
import {
  isPhoneNumber,
  isValidEmail,
  isValidUSZip
} from '@ion/core/src/utils/Validators';
import {isPossiblePhoneNumber} from '@ion/dls/src/components/phone/Utils';
import {AugmentedFormState} from '@ion/dls/src/hooks/useFormState';
import {convertStatus} from '@ion/dls/src/utils/ComponentUtils';
import {BadgeType} from '../types/editors';

// available field names for the add/edit group form
export enum GroupFormFields {
  GroupName = 'name',
  Enterprise = 'enterprise',
  VehicleModule = 'vehicleTestProfileValidation',
  AutojobsEnabled = 'autojobsEnabled',
  ParentGroup = 'parent',
  Status = 'status',
  SolomonId = 'solomonId',
  ExternalRefID = 'externalRefID',
  Subscription = 'subscription',
  UnitOfMeasure = 'unitOfMeasure',
  WebMapName = 'webMapName',
  Timezone = 'timeZone',
  Language = 'language',
  Labels = 'labels',
  OverspeedThreshold = 'overspeedThreshold',
  WorkstopDuration = 'workStopDuration',
  AutoAssetCreation = 'autoAssetCreationEnabled',
  AutoAssetAssociation = 'autoAssetAssociationEnabled',
  AssetTypes = 'assetTypes',
  OperatorTypes = 'operatorTypes',
  AssetCategories = 'assetCategories',
  OperatorCategories = 'operatorCategories',
  RouteCategories = 'routeCategories',
  GeofenceCategories = 'landmarkCategories',
  Industry = 'partnerBrand',
  RelativeSize = 'relativeSize',
  CompanyURL = 'partnerUrl',
  Applications = 'applications',
  Addresses = 'addresses',
  PhoneNumbers = 'phoneNumbers',
  PointOfContacts = 'pointOfContacts',
  AccountContacts = 'accountContacts',
  UrgentNotificationsEnabled = 'urgentNotificationsEnabled',
  NotificationsAlertSoundEnabled = 'notificationsAlertSoundEnabled',
  DateFormat = 'dateFormat',
  TimeFormat = 'timeFormat',
  NightTimeDrivingStart = 'night_start',
  NightTimeDrivingEnd = 'night_end',
  Driverscoring = 'driverScoring',
  MessageEndpoint = 'messageEndpoint',
  EnterpriseApplications = 'enterpriseApplications',
  ApiAccess = 'apiAccess',
  Users = 'users',
  SSOMode = 'ssoMode',
  SSOConfig = 'ssoConfig',
  AccountId = 'accountId',
  ServiceProfile = 'serviceProfile',
  OperatorIdType = 'operatorIdType',
  SFTPServerId = 'sftpServerId',
  DoDeleteMsgEP = 'doDeleteMsgEP',
  HasEmissionTester = 'hasEmissionTester',
  UseCustomVisionSettings = 'useCustomVisionSettings',
  TripCategorizationOverride = 'tripCategorizationOverride',
  TripCategorizationSetting = 'tripCategorizationSetting',
  MultiAuth = 'multiAuth',
  MultiAuthConfigured = 'multiAuthConfigured',
  MultiAuthApplyToAllSubaccounts = 'multiAuthApplyToAllSubaccounts'
};

export enum AccountFeature {
  TripCategorization = 'tripCat',
  MultiAuth = 'multiAuth'
}

export enum MultiAuthStatus {
  Disabled = 'Disabled',
  AccountEnabled = 'AccountEnabled',
  Enabled = 'Enabled',
  PendingDisable = 'PendingDisable',
  PendingAccountEnabled = 'PendingAccountEnabled',
  PendingEnable = 'PendingEnable'
}

// Fields that are used in ChipTextField component.
export const chipTextFields = [
  GroupFormFields.AssetCategories,
  GroupFormFields.AssetTypes,
  GroupFormFields.OperatorTypes,
  GroupFormFields.OperatorCategories,
  GroupFormFields.RouteCategories,
  GroupFormFields.GeofenceCategories
];

export enum CarbEditorFields {
  COMPLIANCE_SPECIALIST_ID = 'complianceSpecialistId',
  COMPLIANCE_SPECIALIST_NAME = 'complianceSpecialistName',
  COMPLIANCE_SPECIALIST_EMAIL = 'complianceSpecialistEmail',
  EMAIL_ADDRESS = 'emailAddress',
  TESTER_ID = 'testerId',
  NAME = 'name'
}

export interface CarbFields {
  complianceSpecialistId: string;
  complianceSpecialistName: string;
  complianceSpecialistEmail: string;
};

export type SerializedArgs = {
  data: AugmentedFormState;
  contact?: Contact;
  includeCarb?: boolean;
  includeSSOConfig?: boolean;
  includeAccountContacts?: boolean;
  includeAutojobsAttribute?: boolean;
};

const required = {required: true};
const notRequired = {required: false};

/**
 * Provides the initial state of the Group editor form, this state includes all named
 * editor fields and their requirements, as well as any initial values required, such
 * as the application for which the group is to be created, or a reference to a group,
 * in the case of edits.
 */
export const getInitialState = (group: Account | GenericObject): GenericObject => {
  return {
    [GroupFormFields.Status]                         : {...required, value: EntityStatus.Enabled},
    [GroupFormFields.AutoAssetCreation]              : {value: true},
    [GroupFormFields.AutoAssetAssociation]           : {value: true},
    [GroupFormFields.UseCustomVisionSettings]        : {value: false},
    [GroupFormFields.GroupName]                      : required,
    [GroupFormFields.ParentGroup]                    : required,
    [GroupFormFields.WorkstopDuration]               : required,
    [GroupFormFields.UrgentNotificationsEnabled]     : {value: false},
    [GroupFormFields.NotificationsAlertSoundEnabled] : {value: false},
    [GroupFormFields.Timezone]                       : {...required, value: null},
    [GroupFormFields.DateFormat]                     : {value: getDefaultDateFormat(getEnvironment())},
    [GroupFormFields.TimeFormat]                     : {value: 'hh:mm A'},
    [GroupFormFields.NightTimeDrivingStart]          : {value: '23:00:00'},
    [GroupFormFields.NightTimeDrivingEnd]            : {value: '05:00:00'},
    [GroupFormFields.OperatorIdType]                 : {value: getOperatorIdType() || BadgeType.Other},
    [GroupFormFields.SFTPServerId]                   : {value: ''},
    [GroupFormFields.TripCategorizationOverride]     : {value: false},
    [GroupFormFields.TripCategorizationSetting]      : {value: EntityStatus.Disabled},
    ...deserialize(group)
  };
};

/**
 * Provides the initial state of the Group editor form for Ctc Admin, this state includes all named
 * editor fields and their requirements, as well as any initial values required, such
 * as the application for which the group is to be created, or a reference to a group,
 * in the case of edits.
 */
export const getInitialCtcAdminState = (
  group: Account | GenericObject, ssoConfiguration?: SSOConfiguration): GenericObject => {
  return {
    [GroupFormFields.Status]                         : {...required, value: EntityStatus.Enabled},
    [GroupFormFields.Applications]                   : {...required, value: []},
    [GroupFormFields.Enterprise]                     : {...notRequired, value: false},
    [GroupFormFields.VehicleModule]                  : {...notRequired, value: false},
    [GroupFormFields.AutojobsEnabled]                : {...notRequired, value: true},
    [GroupFormFields.SolomonId]                      : {...notRequired, value: ''},
    [GroupFormFields.Subscription]                   : {...notRequired, value: ''},
    [GroupFormFields.ServiceProfile]                 : {...notRequired, value: ''},
    [GroupFormFields.ApiAccess]                      : {...notRequired, value: true},
    [GroupFormFields.Timezone]                       : {value: defaultTimezone},
    [GroupFormFields.UnitOfMeasure]                  : {value: defaultUnit},
    [GroupFormFields.AutoAssetCreation]              : {value: false},
    [GroupFormFields.AutoAssetAssociation]           : {value: false},
    [GroupFormFields.UseCustomVisionSettings]        : {value: false},
    [GroupFormFields.TripCategorizationOverride]     : {value: false},
    [GroupFormFields.TripCategorizationSetting]      : {value: EntityStatus.Disabled},
    [GroupFormFields.MultiAuth]                      : {value: false},
    [GroupFormFields.MultiAuthConfigured]            : {value: false},
    [GroupFormFields.MultiAuthApplyToAllSubaccounts] : {value: true},
    [GroupFormFields.Users]                          : notRequired,
    [GroupFormFields.GroupName]                      : required,
    [GroupFormFields.ParentGroup]                    : required,
    [GroupFormFields.SFTPServerId]                   : {value: ''},
    [GroupFormFields.DoDeleteMsgEP]                  : {value: false},
    ...deserialize(group),
    ...getSSOFields(group, ssoConfiguration, false),
    ...deserializeAddress(group)
  };
};

/**
 * Strips an account down to it's minimum {@link Link} fields required by services.
 */
const getAccountLink = ({href, rel, title, status}: AccountLink): Link => {
  return {href, rel, title, status};
};

export const getSSOFields = (group, ssoConfiguration, isRequired = false) => {
  return ssoConfiguration?.id
    ? deserializeSSO(ssoConfiguration, group)
    : {
      [SSOFormFields.SSOMode]           : {...{required: isRequired}, value: 'LAX'},
      [SSOFormFields.DiscoveryEndpoint] : {required: isRequired},
      [SSOFormFields.Domains]           : {required: isRequired},
      [SSOFormFields.APPs]              : {required: isRequired}
    };
};

export const deserializeAddress = (emissionTester: EmissionTester, required = false) => {
  const accountTester = emissionTester || {};
  return {
    hasEmissionTester                              : {value: Object.keys(accountTester)?.length > 0},
    [CarbEditorFields.COMPLIANCE_SPECIALIST_EMAIL] : {
      value    : accountTester?.[CarbEditorFields.EMAIL_ADDRESS] || '',
      error    : false,
      required : required
    },
    [CarbEditorFields.COMPLIANCE_SPECIALIST_ID] : {
      value    : accountTester?.[CarbEditorFields.TESTER_ID] || '',
      error    : false,
      required : required
    },
    [CarbEditorFields.COMPLIANCE_SPECIALIST_NAME] : {
      value    : accountTester?.[CarbEditorFields.NAME] || '',
      error    : false,
      required : required
    }
  };
};

/**
 * This function takes the form data from the asset form and
 * serializes it into a a CTC Group object that gets passed to the
 * addGroup service call.
 *
 * @param {object} data: The form data that needs to be transformed into
 *   an asset that can be passed to ctc to create the asset.
 */
export const serialize = ({
  data,
  contact,
  includeCarb,
  includeAccountContacts,
  includeSSOConfig = false,
  includeAutojobsAttribute = false
}: SerializedArgs) => {
  const accountData: GenericObject = {
    extendedAttribute : {
      value : ''
    }
  };
  const extendedAttributeValue = {};
  const customerProfileObj = {};
  const ssoInfo = {};
  const carbMetaData = {};
  for (const [key, obj] of Object.entries(data)) {
    switch (key) {
      case GroupFormFields.ParentGroup:
        accountData[key] = {
          ...getAccountLink(obj?.value),
          rel : 'parent'
        };
        break;
      case GroupFormFields.Subscription:
        accountData[key] = data.enterprise?.value && obj.value ? setRel(obj.value, 'subscription') : null;
        break;
      case GroupFormFields.VehicleModule:
      case GroupFormFields.Enterprise:
        accountData[key] = obj.value || false;
        break;
      case GroupFormFields.AutojobsEnabled:
        if (includeAutojobsAttribute) {
          extendedAttributeValue[key] = !isNil(obj.value) ? obj.value : includeAutojobsAttribute;
        }
        break;
      case CarbEditorFields.COMPLIANCE_SPECIALIST_EMAIL:
        if (includeCarb) {
          carbMetaData[CarbEditorFields.EMAIL_ADDRESS] = !isNil(obj.value) ? obj.value : {};
        }
        break;
      case CarbEditorFields.COMPLIANCE_SPECIALIST_ID:
        if (includeCarb) {
          carbMetaData[CarbEditorFields.TESTER_ID] = !isNil(obj.value) ? obj.value : {};
        }
        break;
      case CarbEditorFields.COMPLIANCE_SPECIALIST_NAME:
        if (includeCarb) {
          carbMetaData[CarbEditorFields.NAME] = !isNil(obj.value) ? obj.value : {};
        }
        break;
      case GroupFormFields.OverspeedThreshold:
      case GroupFormFields.WorkstopDuration:
      case GroupFormFields.AssetCategories:
      case GroupFormFields.AssetTypes:
      case GroupFormFields.OperatorTypes:
      case GroupFormFields.OperatorCategories:
      case GroupFormFields.RouteCategories:
      case GroupFormFields.GeofenceCategories:
      case GroupFormFields.WebMapName:
      case GroupFormFields.UrgentNotificationsEnabled:
      case GroupFormFields.DateFormat:
      case GroupFormFields.OperatorIdType:
      case GroupFormFields.SFTPServerId:
        extendedAttributeValue[key] = obj.value !== 'none' ? obj.value : null;
        break;
      case GroupFormFields.TimeFormat:
      case GroupFormFields.NightTimeDrivingStart:
      case GroupFormFields.NightTimeDrivingEnd:
        extendedAttributeValue[key] = obj.value;
        break;
      case GroupFormFields.Driverscoring:
        extendedAttributeValue[key] = obj.value ?? obj;
        break;
      case GroupFormFields.NotificationsAlertSoundEnabled:
        extendedAttributeValue[key] = obj.isShadow ? null : obj.value;
        break;
      case GroupFormFields.Timezone:
        accountData[key] = (isObject(obj.value) ? obj.value?.value : obj.value) || null;
        break;
      case GroupFormFields.Labels:
        accountData[key] = obj.value ? [obj.value] : [];
        break;
      case GroupFormFields.AutoAssetCreation:
      case GroupFormFields.AutoAssetAssociation:
      case GroupFormFields.UseCustomVisionSettings:
        accountData[key] = obj.value;
        break;
      case GroupFormFields.TripCategorizationOverride:
      case GroupFormFields.TripCategorizationSetting:
        break;
      case GroupFormFields.Status:
        accountData[key] = convertStatus(obj.value);
        break;
      case GroupFormFields.Industry:
      case GroupFormFields.RelativeSize:
      case GroupFormFields.CompanyURL:
        customerProfileObj[key] = obj.value ? obj.value : null;
        break;
      case GroupFormFields.Language:
        accountData[key] = getLanguageKey(obj.value);
        break;
      case GroupFormFields.MessageEndpoint:
        accountData[key] = serializeMessageEndpoint(obj);
        break;
      case GroupFormFields.ApiAccess:
        accountData[key] =  obj.value;
        break;
      case GroupFormFields.Users:
        accountData[key] = serializeUsers(obj.value);
        break;
      case GroupFormFields.DoDeleteMsgEP:
        accountData[key] =  obj.value;
        break;
      case GroupFormFields.HasEmissionTester:
        accountData[key] =  obj.value;
        break;
      case GroupFormFields.MultiAuth:
      case GroupFormFields.MultiAuthConfigured:
      case GroupFormFields.MultiAuthApplyToAllSubaccounts:
        break;
      case SSOFormFields.Domains:
        if (includeSSOConfig) {
          ssoInfo[key] = getDomainValue(obj.value);
        }
        break;
      case SSOFormFields.DiscoveryEndpoint:
        if (includeSSOConfig) {
          ssoInfo[key] = obj.value;
        }
        break;
      case SSOFormFields.SSOMode:
        if (includeSSOConfig) {
          ssoInfo[key] = obj.value;
        }
        break;
      case SSOFormFields.APPs:
        if (includeSSOConfig) {
          ssoInfo[key] = serializeSSOApps(obj.value);
        }
        break;
      default:
        if (obj?.value) {
          accountData[key] = obj.isShadow ? null : obj.value || null;
        }
    }
  }

  if (Object.keys(extendedAttributeValue).length) {
    accountData.extendedAttribute.value = JSON.stringify(extendedAttributeValue);
  }
  const features = {...(accountData.features || {})};
  if (data?.[GroupFormFields.TripCategorizationOverride]?.value) {
    features[AccountFeature.TripCategorization] =
      data?.[GroupFormFields.TripCategorizationSetting]?.value || EntityStatus.Disabled;
  } else {
    delete features[AccountFeature.TripCategorization];
  }
  const multiAuthRaw     = (data?.features?.value as GenericObject)?.[AccountFeature.MultiAuth];
  const multiAuthPending = [
    MultiAuthStatus.PendingEnable,
    MultiAuthStatus.PendingDisable,
    MultiAuthStatus.PendingAccountEnabled
  ].includes(multiAuthRaw);
  if (!multiAuthPending && data?.[GroupFormFields.MultiAuthConfigured]?.value) {
    if (data?.[GroupFormFields.MultiAuth]?.value) {
      features[AccountFeature.MultiAuth] = data?.[GroupFormFields.MultiAuthApplyToAllSubaccounts]?.value
        ? MultiAuthStatus.Enabled
        : MultiAuthStatus.AccountEnabled;
    } else {
      features[AccountFeature.MultiAuth] = MultiAuthStatus.Disabled;
    }
  }
  if (Object.keys(features).length) {
    accountData.features = features;
  } else {
    delete accountData.features;
  }
  if (Object.keys(customerProfileObj).length) {
    accountData.customerProfile = customerProfileObj;
  }
  if (Object.keys(ssoInfo).length) {
    accountData.ssoInfo = ssoInfo;
  }
  if (carbMetaData && Object.keys(carbMetaData)?.length) {
    accountData.carbMetaData = carbMetaData;
  }
  if (includeAccountContacts && contact?.show) {
    accountData.accountContacts = serializeAccountContacts(contact);
  }
  if (data?.enterprise?.value) {
    const currentAccountId = parseInt(data?.parent?.value?.href?.split('/')?.reverse()[0], 10);
    const isRootAccount    = currentAccountId === Number(get('rootAccountId'));
    if (!isRootAccount) {
      accountData.inheritAllSubscriptionsFromParent = true;
    }
  }
  if (!data.enterprise?.value) {
    delete accountData.apiAccess;
    delete accountData.users;
  }
  return accountData;
};

/**
 * This function takes the data then serializes it into a a CTC Contact object
 * that gets passed to the addContact/editContact service call.
 *
 * @param { object } contact: Object needs to serialize
  * an asset that can be passed to ctc to create the asset.
 */
const serializeAccountContacts = (contact: GenericObject) => {
  const addressData = contact?.addresses;
  const phoneData = contact?.phoneNumbers;
  const addresses = addressData?.map((item: { addresses: any; }) => item.addresses);
  const finalAddress = addresses.map((address: any) => {
    const addressObj = {...address};
    addressObj.addressType = addressObj.addressType?.toUpperCase();
    return addressObj;
  });
  const phoneNumber = phoneData?.map((item: { phoneNumbers: any; }) => item.phoneNumbers);
  const finalContact = phoneNumber.map((contact: any) => {
    const phoneObj = {...contact};
    phoneObj.phoneType = phoneObj.phoneType?.toUpperCase();
    return phoneObj;
  });
  const finalAccountContact = {...contact};
  finalAccountContact.phoneNumbers = finalContact;
  finalAccountContact.addresses = finalAddress;
  return finalAccountContact;
};

/**
 * This function takes a CTC account and serializes it into
 * a group form consumable object that can be pre-populated
 * into the form. Essentially, it's the reverse of the serialize
 * function above.
 *
 * @param {Account} group: CTC account that is to be deserialized into
 *   a group form object.
 */
export const deserialize = (group: Account | GenericObject) => {
  if (Object.keys(group || {}).length) {
    const formData = {};
    for (const [key, obj] of Object.entries(group)) {
      switch (key) {
        case 'extendedAttribute': {
          obj && parseAttributes(obj, formData);
          break;
        }
        case 'assetCategories':
        case 'operatorCategories':
        case 'routeCategories':
        case 'landmarkCategories':
          break;
        case 'customerProfile': {
          obj && getCustomerProfile(obj, formData);
          break;
        }
        case 'features': {
          formData[key] = {
            name  : key,
            value : obj
          };
          formData[GroupFormFields.TripCategorizationOverride] = {
            name  : GroupFormFields.TripCategorizationOverride,
            value : Object.prototype.hasOwnProperty.call(obj || {}, AccountFeature.TripCategorization)
          };
          formData[GroupFormFields.TripCategorizationSetting] = {
            name  : GroupFormFields.TripCategorizationSetting,
            value : obj?.[AccountFeature.TripCategorization] === EntityStatus.Enabled
              ? EntityStatus.Enabled : EntityStatus.Disabled
          };
          const multiAuthValue = obj?.[AccountFeature.MultiAuth];
          formData[GroupFormFields.MultiAuth] = {
            name  : GroupFormFields.MultiAuth,
            value : [
              MultiAuthStatus.AccountEnabled,
              MultiAuthStatus.Enabled,
              MultiAuthStatus.PendingAccountEnabled,
              MultiAuthStatus.PendingEnable
            ].includes(multiAuthValue)
          };
          formData[GroupFormFields.MultiAuthApplyToAllSubaccounts] = {
            name  : GroupFormFields.MultiAuthApplyToAllSubaccounts,
            value : [MultiAuthStatus.Enabled, MultiAuthStatus.PendingEnable].includes(multiAuthValue)
          };
          formData[GroupFormFields.MultiAuthConfigured] = {
            name  : GroupFormFields.MultiAuthConfigured,
            value : Object?.prototype?.hasOwnProperty?.call?.(obj || {}, AccountFeature.MultiAuth)
          };
          break;
        }
        default: {
          formData[key] = {
            name  : key,
            value : getDeserializedvalue(key, obj)
          };
        }
      }
    }
    return formData;
  }
  return {};
};

/**
 * The private function do special deserializing(if any) of PhoneNumbers object
 *
 * @param contacts CTC deserialized object name
 * @returns Deserialized value
 */
export const getDeserializedContacts = (contacts) => {
  const phoneArr = [];
  contacts?.map((contact, ind) => {
    const obj = {
      ...contact,
      isExtensionNumError : false,
      isPhoneNumError     : false,
      PhoneError          : false
    };
    phoneArr.push({id: ind + 1, phoneNumbers: obj});
  });
  return phoneArr;
};

/**
 * The private function do special deserializing(if any) of PhoneNumbers object
 *
 * @param contacts CTC deserialized object name
 * @returns Deserialized value
 */
export const getDeserializedAddress = (addresses) => {
  const addressArr = [];
  addresses?.map((item, ind) => {
    const obj = {
      ...item,
      isStreetError     : false,
      isStateError      : false,
      isCityError       : false,
      isZipError        : false,
      isZipInvalidError : false,
      isCountryError    : false
    };
    addressArr.push({id: ind + 1, addresses: obj});
  });
  return addressArr;
};

/**
 * The private function do special deserializing(if any) of the CTC Group object
 *
 * @param key CTC deserialized object name
 * @param value The actual value of CTC deserialized object
 * @returns Deserialized value
 */
const getDeserializedvalue = (key: string, value: number | string | GenericObject):
number | string | GenericObject => {
  if (typeof value === 'string' && !isEmpty(value) &&
    chipTextFields.indexOf(key as GroupFormFields) !== -1) {
    return value?.split(',');
  }
  if (key === 'labels' && typeof value === 'object') {
    return isArray(value) ? value.join() : value;
  }
  if (key === 'language') {
    return getLanguageKey(value as string);
  }
  if (key === GroupFormFields.Subscription && typeof value === 'object' && value?.href) {
    return {...value, id: entityId(value.href)};
  }
  return value;
};

export const parseAttributes = (obj: { value?: string } = {}, formData) => {
  const extendedAttributes = parseExtendedAttributes({extendedAttribute: obj}, true);
  if (!isObjectEmpty(extendedAttributes)) {
    for (const [key, obj] of Object.entries(extendedAttributes)) {
      formData[key] = {
        name  : key,
        value : Array.isArray(obj) && obj.length === 0 ? '' : obj
      };
    };
  }
};

const getCustomerProfile = (custObj: { value?: string } = {}, formData) => {
  if (!isObjectEmpty(custObj)) {
    for (const [key, obj] of Object.entries(custObj)) {
      formData[key] = {
        name  : key,
        value : obj
      };
    };
  }
};

// This function validate the Contacts component
export const validateContacts = (contactInfo: any[], isCtcAdmin = false) => {
  let isError = true;
  const tempContact = contactInfo?.map((item) => {
    const phone           = item?.phoneNumbers?.phoneNumber;
    if (!phone) {
      item.phoneNumbers.PhoneError = true;
      isError = false;
    } else {
      item.phoneNumbers.PhoneError = false;
    }
    if (phone && (isCtcAdmin ? !isPossiblePhoneNumber(phone) : !isPhoneNumber(phone))) {
      item.phoneNumbers.isPhoneNumError = true;
      isError = false;
    } else {
      item.phoneNumbers.isPhoneNumError = false;
    }
    return item;
  });
  return {
    isValidContact : isError,
    contactsArr    : tempContact
  };
};

// This function validate the Addresses component
export const validateAddresses = (addresses: any[]) => {
  let isError = true;
  const tempAddresses = addresses?.map((items) => {
    if (items?.addresses.street === '') {
      items.addresses.isStreetError = true;
      isError = false;
    } else {
      items.addresses.isStreetError = false;
    }
    if (items?.addresses.city === '') {
      items.addresses.isCityError = true;
      isError = false;
    } else {
      items.addresses.isCityError = false;
    }
    if (items?.addresses.stateProvince === '' || items?.addresses.stateProvince === 'State') {
      items.addresses.isStateError = true;
      isError = false;
    } else {
      items.addresses.isStateError = false;
    }
    if (items?.addresses.country === '') {
      items.addresses.isCountryError = true;
      isError = false;
    } else {
      items.addresses.isCountryError = false;
    }
    if (items?.addresses.zip === '') {
      items.addresses.isZipError = true;
      isError = false;
    } else {
      items.addresses.isZipError = false;
    }
    const {countryCd, zip} = items?.addresses || {};
    if (countryCd === 'US') {
      const isValidZip = isValidUSZip(zip);
      items.addresses.isZipInvalidError = isValidZip === false;
      if (!isValidZip) {
        isError = false;
      }
    } else {
      items.addresses.isZipInvalidError = false;
    }
    return items;
  });
  return {
    isValidAddress : isError,
    addressesArr   : tempAddresses
  };
};

// This function validate the AccountContacts component
export const validateAccountContacts = (contact: GenericObject, isCtcAdmin = false) => {
  let isError = true;
  const contactInfo = cloneDeep(contact);
  if (contactInfo?.firstName === '') {
    contactInfo.isFirstNameError = true;
    isError = false;
  } else {
    contactInfo.isFirstNameError = false;
  }
  if (contactInfo?.lastName === '') {
    contactInfo.isLastNameError = true;
    isError = false;
  } else {
    contactInfo.isLastNameError = false;
  }
  if (contactInfo?.email === '') {
    contactInfo.isEmailError = true;
    isError = false;
  } else if (contactInfo?.email !== '' && !isValidEmail(contactInfo?.email)) {
    contactInfo.isEmailError = true;
    isError = false;
  } else {
    contactInfo.isEmailError = false;
  }
  if (isCtcAdmin && contactInfo?.phoneNumbers.length > 0) {
    contactInfo.isPhoneError = false;
  } else {
    contactInfo.isPhoneError = true;
    isError = false;
  }
  const {isValidContact, contactsArr} = validateContacts(contact.phoneNumbers, isCtcAdmin);
  const {isValidAddress, addressesArr} = validateAddresses(contact.addresses);
  contactInfo.phoneNumbers = contactsArr;
  contactInfo.addresses = addressesArr;
  if (!isValidContact) {
    isError = false;
  }
  if (!isValidAddress) {
    isError = false;
  }

  return {
    isValid     : isError,
    contactInfo : contactInfo
  };
};

// This function validates the Users component
export const validateUsers = (userInfo: Entry[]) => {
  let isError = true;
  const tempUsers = userInfo?.map((item) => {
    const user = item.user;
    const {firstName, lastName, email, phoneNumber} = user;
    if (firstName === '') {
      user.isFirstNameError = true;
      isError = false;
    } else {
      user.isFirstNameError = false;
    }
    if (lastName === '') {
      user.isLastNameError = true;
      isError = false;
    } else {
      user.isLastNameError = false;
    }
    if (email === '') {
      user.isEmailError = true;
      isError = false;
    } else if (email !== '' && !isValidEmail(email)) {
      user.isEmailError = true;
      isError = false;
    } else {
      user.isEmailError = false;
    }
    if (phoneNumber === '') {
      user.isPhoneError = true;
      isError = false;
    } else if (phoneNumber && !isPossiblePhoneNumber(phoneNumber)) {
      user.isPhoneError = true;
      isError = false;
    } else {
      user.isPhoneError = false;
    }
    return item;
  });
  return {
    isValid  : isError,
    userList : tempUsers
  };
};

export const serializeUsers = (userData: Entry[]) => {
  const users = userData?.map(({user}) => user);
  const columns = ['firstName', 'lastName', 'email', 'phoneNumber'];
  const userList = users?.map((item) => {
    const userObj = {
      user : {
        firstName    : '',
        lastName     : '',
        email        : '',
        phoneNumbers : []
      }
    };
    columns.forEach(key => {
      if (key === 'phoneNumber') {
        const phoneArr = [
          {[key]: item[key], phoneType: 'CELL'}
        ];
        userObj.user.phoneNumbers = phoneArr;
      } else {
        userObj.user[key] = item[key];
      }
    });
    return userObj;
  });
  return userList;
};

//defines the ADM Default Application keys
export const CTC_APPLICATIONS = {
  ADM              : '820b5258-1f73-4d7b-bd72-c75c4e5072d1',
  CONNECT_API_DOCS : 'd8b98aad-cc6e-4899-a86d-e2848eaf03b4',
  DMADMIN          : 'd3b23f65-bff5-40b0-b821-dceaa3b8cf38',
  ION              : '82c904bd-71a8-4d38-a645-e6980510b7e2',
  CACTC            : '82c904bd-71a8-4d38-a645-e6980510b7e2'
};

export const cactcAppKey = '82c904bd-71a8-4d38-a645-e6980510b7e2';
export const defaultTimezone = 'America/Los_Angeles';
export const defaultUnit = 'Default';

export const getInitialApplications = (applications) => {
  const ctcApplicationKeys = Object.values(CTC_APPLICATIONS);
  const defaultApplications = ctcApplicationKeys.slice(0, 3);
  const appNames = [];
  const appHrefs = [];
  applications?.forEach((application) => {
    const {name, applicationKey} = application;
    if (defaultApplications.indexOf(applicationKey) > -1) {
      const hrefObj = setRel(application, 'application');
      appNames.push(name);
      appHrefs.push({
        ...hrefObj,
        title : name
      });
    }
  });
  return {appNames, appHrefs};
};

export const getMemberIds = (members) => {
  return members?.map((member) => member.id);
};
